/**
 * Teslyn Cloud's own address for a document (MB's R235, 5 Nov 2026; docs/specs/cloud/document-viewer.md): a person
 * reads a PDF or photo at `?page=document&doc=...` on the portal's address, never at a storage address. The link says
 * which document, not where it is stored: it holds no token and gives nothing without the person's own sign-in, and a
 * link kept open or reloaded finds the document as it is now.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NAME_MAX = 200;

export const DOCUMENT_PAGE = 'document';

/** A document the viewer can open. */
export type DocumentRef =
  /** A daily or weekly report's PDF, by its report. */
  | { readonly doc: 'report'; readonly id: string }
  /** The project's latest height breach report PDF. */
  | { readonly doc: 'breach-report'; readonly project: string }
  /** A fuel resupply's delivery docket, by its ledger entry. */
  | { readonly doc: 'fuel-docket'; readonly id: string }
  /**
   * The display size of a photo linked to a safety register entry. Clients do not read photo rows, so the link
   * carries the operator's organisation, and the name the page shows.
   */
  | {
      readonly doc: 'register-photo';
      readonly org: string;
      readonly project: string;
      readonly id: string;
      readonly name: string;
    };

/** `?tenant=` on a preview host stays on the link. */
export function documentPageSearch(ref: DocumentRef, currentSearch = ''): string {
  const q = new URLSearchParams({ page: DOCUMENT_PAGE, doc: ref.doc });
  if (ref.doc === 'breach-report') q.set('project', ref.project);
  else if (ref.doc === 'register-photo') {
    q.set('org', ref.org);
    q.set('project', ref.project);
    q.set('id', ref.id);
    q.set('name', ref.name.slice(0, NAME_MAX));
  } else q.set('id', ref.id);
  const tenant = new URLSearchParams(currentSearch).get('tenant');
  if (tenant) q.set('tenant', tenant);
  return `?${q.toString()}`;
}

/** Does this address ask for the viewer (a complete link or not)? */
export function isDocumentPage(search: string): boolean {
  return new URLSearchParams(search).get('page') === DOCUMENT_PAGE;
}

/** The document a link names, or null when it is not the viewer's link or is incomplete. */
export function documentRefOfSearch(search: string): DocumentRef | null {
  const q = new URLSearchParams(search);
  if (q.get('page') !== DOCUMENT_PAGE) return null;
  const uuid = (key: string): string | null => {
    const v = q.get(key);
    return v && UUID.test(v) ? v : null;
  };
  const doc = q.get('doc');
  const id = uuid('id');
  const project = uuid('project');
  const org = uuid('org');
  if (doc === 'report' || doc === 'fuel-docket') return id ? { doc, id } : null;
  if (doc === 'breach-report') return project ? { doc, project } : null;
  if (doc === 'register-photo')
    return id && project && org
      ? { doc, org, project, id, name: (q.get('name') ?? '').slice(0, NAME_MAX) }
      : null;
  return null;
}

/** What the viewer does with a file. */
export type DocumentShape = 'pdf' | 'image' | 'other';

/** Stored PDFs do not always carry their type, so the file name counts too. */
export function documentShape(fileName: string, contentType: string): DocumentShape {
  if (contentType === 'application/pdf' || /\.pdf$/i.test(fileName)) return 'pdf';
  if (contentType.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(fileName)) return 'image';
  return 'other';
}

/** The browser facts that decide how a PDF is shown. */
export interface PdfBrowser {
  readonly pdfViewerEnabled?: boolean;
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
}

/**
 * `browser`: the browser's own viewer in the page (Chrome, Edge, Safari on a Mac), with its zoom, search and print.
 * `pages`: Teslyn draws the pages. An iPad or iPhone shows only part of a PDF inside a page, and a phone browser
 * without a viewer shows none. An iPad says it is a Mac, and only a Mac with a touch screen gives it away.
 */
export function pdfViewFor(b: PdfBrowser): 'browser' | 'pages' {
  const ios = /iPad|iPhone|iPod/.test(b.userAgent) || (b.platform === 'MacIntel' && b.maxTouchPoints > 1);
  return !ios && b.pdfViewerEnabled === true ? 'browser' : 'pages';
}
